import { jsPDF } from "jspdf";

const formatCurrency = (val) => {
  const num = Number(val || 0);
  const fixed = num.toFixed(2);
  const [intPart, decPart] = fixed.split(".");
  const lastThree = intPart.slice(-3);
  const rest = intPart.slice(0, -3);
  const grouped =
    rest.length > 0
      ? rest.replace(/\B(?=(\d{2})+(?!\d))/g, ",") + "," + lastThree
      : lastThree;
  return "Rs." + grouped + "." + decPart;
};

const today = () => {
  const d = new Date();
  return d.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const loadImageAsBase64 = (url) =>
  new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      canvas.getContext("2d").drawImage(img, 0, 0);
      resolve(canvas.toDataURL("image/png"));
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });

/**
 * Returns the rendered pixel dimensions of a base64 image.
 * Used to calculate the aspect-ratio-correct height for the PDF.
 */
const getImageDimensions = (dataUrl) =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight });
    img.onerror = () => resolve({ w: 1, h: 1 });
    img.src = dataUrl;
  });

export const generateQuotationPDF = async ({
  enquiryData,
  currentStageCustomPricing = false,
  companyLogo = null,
  companyName = "Your Company",
  // noteImagePairs: Array<{ id, note, imageDataUrl }>
  noteImagePairs = [],
  taxBreakdown = {},
}) => {
  const doc = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });
  const PW = 210;
  const MARGIN = 16;
  const CONTENT_W = PW - MARGIN * 2;
  let y = 0;

  const BRAND = [59, 130, 246];
  const DARK = [26, 32, 44];
  const MUTED = [113, 128, 150];
  const LIGHT = [237, 242, 247];
  const WHITE = [255, 255, 255];
  const RED = [229, 62, 62];
  const REMARKS = [74, 85, 104];

  const setFont = (weight = "normal", size = 10, color = DARK) => {
    doc.setFont("helvetica", weight);
    doc.setFontSize(size);
    doc.setTextColor(...color);
  };

  const fillRect = (x, ry, w, h, color) => {
    doc.setFillColor(...color);
    doc.rect(x, ry, w, h, "F");
  };

  const line = (x1, ly, x2, color = LIGHT, lw = 0.3) => {
    doc.setDrawColor(...color);
    doc.setLineWidth(lw);
    doc.line(x1, ly, x2, ly);
  };

  /** Ensure there is at least `needed` mm left on the current page. */
  const ensureSpace = (needed) => {
    if (y + needed > 272) {
      doc.addPage();
      y = MARGIN;
    }
  };

  /** Approx. mm needed per wrapped text line at a given font size
   *  (jsPDF's default lineHeightFactor is 1.15). */
  const mmPerLine = (fontSize) => (fontSize * 1.15 * 25.4) / 72;

  // ── 1. HEADER BAND ────────────────────────────────────────────────────────
  const HEADER_H = 40;
  fillRect(0, 0, PW, HEADER_H, BRAND);
  y = HEADER_H;

  let logoLoaded = false;
  const LOGO_SIZE = 24;
  const LOGO_TOP = (HEADER_H - LOGO_SIZE) / 2;

  if (companyLogo) {
    try {
      const b64 = companyLogo.startsWith("data:")
        ? companyLogo
        : await loadImageAsBase64(companyLogo);
      if (b64) {
        doc.setFillColor(...WHITE);
        doc.roundedRect(
          MARGIN - 2,
          LOGO_TOP - 2,
          LOGO_SIZE + 4,
          LOGO_SIZE + 4,
          3,
          3,
          "F",
        );
        doc.addImage(
          b64,
          "PNG",
          MARGIN,
          LOGO_TOP,
          LOGO_SIZE,
          LOGO_SIZE,
          undefined,
          "FAST",
        );
        logoLoaded = true;
      }
    } catch (_) {}
  }

  const nameX = logoLoaded ? MARGIN + LOGO_SIZE + 7 : MARGIN;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.setTextColor(...WHITE);
  doc.text(companyName, nameX, HEADER_H / 2 - 0.5);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11.2);
  doc.setTextColor(179, 209, 240);
  doc.text("Quotation", nameX, HEADER_H / 2 + 6.5);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.5);
  doc.setTextColor(179, 209, 240);
  doc.text(`Date: ${today()}`, PW - MARGIN, 22, { align: "right" });

  y += 8;
  line(MARGIN, y, PW - MARGIN, MUTED);
  y += 6;

  // ── 2. ITEMS TABLE ────────────────────────────────────────────────────────
  // All font sizes scaled to 80% of company name title (14pt → base 11.2pt)
  const TBL_HEADER_FONT = 9.5; // table column headers
  const TBL_BODY_FONT = 9.5; // product name, qty, price, amount
  const TBL_SPEC_FONT = 8.5; // spec text (slightly smaller)
  const ROW_H = 9; // minimum row height in mm (grows for wrapped content)
  const ROW_TOP_PAD = 6; // baseline offset from top of row
  const ROW_BOTTOM_PAD = 3; // breathing room below the last wrapped line

  const COL = {
    num: { x: MARGIN, w: 8 },
    prod: { x: MARGIN + 8, w: 40 },
    spec: { x: MARGIN + 48, w: 56 },
    qty: { x: MARGIN + 104, w: 14 },
    price: { x: MARGIN + 118, w: 28 },
    amt: { x: MARGIN + 146, w: 32 },
  };

  fillRect(MARGIN, y, CONTENT_W, ROW_H, [45, 55, 72]);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(TBL_HEADER_FONT);
  doc.setTextColor(...WHITE);
  doc.text("#", COL.num.x + 2, y + 6);
  doc.text("Product", COL.prod.x + 2, y + 6);
  doc.text("Specifications", COL.spec.x + 2, y + 6);
  doc.text("Qty", COL.qty.x + 2, y + 6);
  doc.text("Unit Price", COL.price.x + 2, y + 6);
  doc.text("Amount", COL.amt.x + 2, y + 6);
  y += ROW_H;

  const products = enquiryData.products || [];
  let subtotalBeforeTax = 0;

  products.forEach((product, idx) => {
    const unitPrice = currentStageCustomPricing
      ? product.price || 0
      : product._displayInfo?.sellingPrice || 0;
    const qty = Number(product.quantity) || 0;
    const lineBase = unitPrice * qty;
    const discountAmt =
      enquiryData.discountPercentage > 0
        ? lineBase * (enquiryData.discountPercentage / 100)
        : 0;
    const lineTotal = lineBase - discountAmt;

    subtotalBeforeTax += lineTotal;

    const productName = product._displayInfo?.productName || "Product";

    // One spec attribute per line (model / color / Sq.Mt / etc.) instead of
    // a single comma-joined line — change the join to ", " if you'd rather
    // have it word-wrap as one flowing paragraph.
    const specs = (product._displayInfo?.specs || [])
      .map((s) => `${s.specificationName}: ${s.value}`)
      .join("\n");

    // Wrap both product name and specs; the row grows to fit whichever is taller.
    const prodLines = doc.splitTextToSize(productName, COL.prod.w - 4);
    const specLines = specs ? doc.splitTextToSize(specs, COL.spec.w - 4) : [];

    const prodBlockH = prodLines.length * mmPerLine(TBL_BODY_FONT);
    const specBlockH = specLines.length * mmPerLine(TBL_SPEC_FONT);
    const rowH = Math.max(
      ROW_H,
      Math.max(prodBlockH, specBlockH) +
        ROW_TOP_PAD -
        mmPerLine(TBL_BODY_FONT) +
        ROW_BOTTOM_PAD,
    );

    ensureSpace(rowH);

    const isEven = idx % 2 === 0;
    fillRect(MARGIN, y, CONTENT_W, rowH, isEven ? WHITE : [247, 250, 252]);

    setFont("bold", TBL_BODY_FONT, DARK);
    doc.text(String(idx + 1), COL.num.x + 2, y + ROW_TOP_PAD);

    setFont("normal", TBL_BODY_FONT, DARK);
    doc.text(prodLines, COL.prod.x + 2, y + ROW_TOP_PAD);

    setFont("normal", TBL_SPEC_FONT, MUTED);
    if (specLines.length) {
      doc.text(specLines, COL.spec.x + 2, y + ROW_TOP_PAD);
    }

    setFont("normal", TBL_BODY_FONT, DARK);
    doc.text(String(qty), COL.qty.x + 2, y + ROW_TOP_PAD);
    doc.text(formatCurrency(unitPrice), COL.price.x + 2, y + ROW_TOP_PAD);

    setFont("bold", TBL_BODY_FONT, DARK);
    doc.text(formatCurrency(lineTotal), COL.amt.x + 2, y + ROW_TOP_PAD);

    y += rowH;
  });

  line(MARGIN, y, PW - MARGIN, [203, 213, 224], 0.4);
  y += 7;

  // ── 3. SUMMARY BLOCK ─────────────────────────────────────────────────────
  const SUMMARY_FONT = 11.2; // 80% of 14pt company name
  const SUMMARY_ROW_H = 7.5;
  const summaryX = MARGIN + CONTENT_W * 0.45;
  const summaryW = CONTENT_W * 0.55;

  const drawSummaryRow = (label, value, isBold = false, color = DARK) => {
    setFont(isBold ? "bold" : "normal", SUMMARY_FONT, MUTED);
    doc.text(label, summaryX, y);
    setFont(isBold ? "bold" : "normal", SUMMARY_FONT, color);
    doc.text(value, MARGIN + CONTENT_W, y, { align: "right" });
    y += SUMMARY_ROW_H;
  };

  // Subtotal
  drawSummaryRow("Subtotal:", formatCurrency(subtotalBeforeTax));

  if (enquiryData.discountPercentage > 0) {
    const discountTotal = products.reduce((sum, p) => {
      const price = currentStageCustomPricing
        ? p.price || 0
        : p._displayInfo?.sellingPrice || 0;
      return (
        sum +
        price *
          (Number(p.quantity) || 0) *
          (enquiryData.discountPercentage / 100)
      );
    }, 0);
    drawSummaryRow(
      `Discount (${enquiryData.discountPercentage}%):`,
      `-${formatCurrency(discountTotal)}`,
      false,
      RED,
    );
  }

  // SGST + CGST — one pair per distinct tax rate
  const totalTax = Object.values(taxBreakdown).reduce((s, v) => s + v, 0);
  Object.entries(taxBreakdown).forEach(([rate, taxAmt]) => {
    const half = taxAmt / 2;
    const halfRate = Number(rate) / 2;
    const halfRateLabel =
      halfRate % 1 === 0 ? halfRate.toFixed(0) : halfRate.toFixed(1);
    drawSummaryRow(`SGST (${halfRateLabel}%):`, formatCurrency(half));
    drawSummaryRow(`CGST (${halfRateLabel}%):`, formatCurrency(half));
  });

  line(summaryX, y, PW - MARGIN, [203, 213, 224], 0.5);
  y += 5;

  const grandTotal = subtotalBeforeTax + totalTax;
  const GT_H = 13;

  fillRect(summaryX, y, summaryW, GT_H, BRAND);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(SUMMARY_FONT);
  doc.setTextColor(...WHITE);
  doc.text("Grand Total:", summaryX + 4, y + GT_H / 2 + 2);
  doc.text(
    formatCurrency(grandTotal),
    MARGIN + CONTENT_W - 3,
    y + GT_H / 2 + 2,
    {
      align: "right",
    },
  );
  y += GT_H + 8;

  // ── 4. MAIN NOTES ─────────────────────────────────────────────────────────
  if (enquiryData.notes && enquiryData.notes.trim()) {
    ensureSpace(20);

    line(MARGIN, y, PW - MARGIN, MUTED, 0.3);
    y += 5;

    setFont("bold", 8, MUTED);
    doc.text("NOTES", MARGIN, y);
    y += 5;

    setFont("normal", 8.5, REMARKS);
    const noteLines = doc.splitTextToSize(enquiryData.notes.trim(), CONTENT_W);
    doc.text(noteLines, MARGIN, y);
    y += noteLines.length * 5 + 4;
  }

  // ── 5. NOTE + IMAGE PAIRS ─────────────────────────────────────────────────
  // Each pair renders: a note block followed immediately by its image (if any).
  // Pairs are separated by a subtle divider. Images are NOT sent to the server.

  const validPairs = noteImagePairs.filter(
    (p) => (p.note && p.note.trim()) || p.imageDataUrl,
  );

  if (validPairs.length > 0) {
    ensureSpace(14);

    line(MARGIN, y, PW - MARGIN, MUTED, 0.3);
    y += 5;

    setFont("bold", 8, MUTED);
    doc.text("ADDITIONAL NOTES & REFERENCES", MARGIN, y);
    y += 7;

    for (let i = 0; i < validPairs.length; i++) {
      const pair = validPairs[i];

      // ── Note text ──
      if (pair.note && pair.note.trim()) {
        ensureSpace(12);

        // Small index badge
        setFont("bold", 7.5, BRAND);
        doc.text(`${i + 1}.`, MARGIN, y);

        setFont("normal", 8.5, REMARKS);
        const pairNoteLines = doc.splitTextToSize(
          pair.note.trim(),
          CONTENT_W - 8,
        );
        doc.text(pairNoteLines, MARGIN + 6, y);
        y += pairNoteLines.length * 5 + 3;
      }

      // ── Image ──
      if (pair.imageDataUrl) {
        try {
          const { w: imgW, h: imgH } = await getImageDimensions(
            pair.imageDataUrl,
          );

          // Scale image to fit within content width (max 140 mm wide)
          const MAX_IMG_W = Math.min(CONTENT_W, 140);
          const scale = MAX_IMG_W / imgW;
          const pdfImgW = MAX_IMG_W;
          const pdfImgH = imgH * scale;

          // Convert px → mm (96 dpi assumed; 1 px = 0.2646 mm)
          const pdfImgWmm = pdfImgW; // already in mm-scale
          const pdfImgHmm = Math.min(pdfImgH * 0.2646, 120); // cap height at 120 mm

          ensureSpace(pdfImgHmm + 6);

          // Light border behind image
          doc.setDrawColor(...LIGHT);
          doc.setLineWidth(0.4);
          doc.rect(MARGIN, y, pdfImgWmm, pdfImgHmm);

          doc.addImage(
            pair.imageDataUrl,
            "PNG",
            MARGIN,
            y,
            pdfImgWmm,
            pdfImgHmm,
            undefined,
            "FAST",
          );

          y += pdfImgHmm + 5;
        } catch (_) {
          // If image loading fails, silently skip it
        }
      }

      // Divider between pairs (not after last one)
      if (i < validPairs.length - 1) {
        ensureSpace(8);
        line(MARGIN + 6, y, PW - MARGIN, [220, 226, 234], 0.25);
        y += 6;
      }
    }

    y += 4;
  }

  // ── 6. FOOTER ─────────────────────────────────────────────────────────────
  const FOOTER_Y = 287;
  line(MARGIN, FOOTER_Y - 5, PW - MARGIN, LIGHT, 0.3);
  setFont("normal", 7, MUTED);
  doc.text("This is a computer-generated quotation.", MARGIN, FOOTER_Y);
  doc.text(companyName, PW - MARGIN, FOOTER_Y, { align: "right" });

  // ── Save ──────────────────────────────────────────────────────────────────
  const filename = `Quotation_${enquiryData.clientCompanyName || "client"}.pdf`
    .replace(/\s+/g, "_")
    .replace(/[^a-zA-Z0-9_\-.]/g, "");

  doc.save(filename);
};
